import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../shared/material.module';
import { RouterLink } from '@angular/router';
import { LargeShareholderHoldingsResponseDto, LargeShareholderHoldingDto } from '../../../models/api/largeShareholderHoldingsDto';

export interface HoldingsByYear {
  year: number;
  holdings: LargeShareholderHoldingDto[];
}

export interface HoldingsByCompany {
  companyId: string;
  companyName: string;
  holdings: LargeShareholderHoldingDto[];
}

@Component({
  selector: 'app-large-shareholder-holdings',
  standalone: true,
  imports: [CommonModule, MaterialModule, RouterLink],
  templateUrl: './large-shareholder-holdings.component.html',
  styleUrls: ['./large-shareholder-holdings.component.scss']
})
export class LargeShareholderHoldingsComponent {
  @Input() set holdings(value: LargeShareholderHoldingsResponseDto | null) {
    this._holdings = value;
    this.holdingsByYear = this.groupHoldingsByYear(value?.holdings || []);
    this.holdingsByCompany = this.groupHoldingsByCompany(value?.holdings || []);
  }

  _holdings: LargeShareholderHoldingsResponseDto | null = null;
  holdingsViewMode: 'year' | 'company' = 'year';
  holdingsByYear: HoldingsByYear[] = [];
  holdingsByCompany: HoldingsByCompany[] = [];

  holdingsByYearDisplayedColumns: string[] = [
    'companyName', 'firstClassSharesPercentage', 'firstClassShares',
    'secondClassSharesPercentage', 'secondClassShares',
    'thirdClassSharesPercentage', 'thirdClassShares'
  ];

  holdingsByCompanyDisplayedColumns: string[] = [
    'year', 'firstClassSharesPercentage', 'firstClassShares',
    'secondClassSharesPercentage', 'secondClassShares',
    'thirdClassSharesPercentage', 'thirdClassShares'
  ];

  private groupHoldingsByYear(holdings: LargeShareholderHoldingDto[]): HoldingsByYear[] {
    const map = new Map<number, LargeShareholderHoldingDto[]>();
    for (const h of holdings) {
      if (!map.has(h.year)) {
        map.set(h.year, []);
      }
      map.get(h.year)!.push(h);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => b - a)
      .map(([year, items]) => ({
        year,
        holdings: items.sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''))
      }));
  }

  private groupHoldingsByCompany(holdings: LargeShareholderHoldingDto[]): HoldingsByCompany[] {
    const map = new Map<string, { companyName: string; holdings: LargeShareholderHoldingDto[] }>();
    for (const h of holdings) {
      if (!map.has(h.companyId)) {
        map.set(h.companyId, { companyName: h.companyName, holdings: [] });
      }
      map.get(h.companyId)!.holdings.push(h);
    }
    return Array.from(map.entries())
      .sort(([, a], [, b]) => a.companyName.localeCompare(b.companyName))
      .map(([companyId, data]) => ({
        companyId,
        companyName: data.companyName,
        holdings: data.holdings.sort((a, b) => b.year - a.year)
      }));
  }

  formatPercentage(value?: number | null): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  }

  formatNumber(value?: number | null): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
}
